import React from 'react';
import { Sparkles, Target, Heart, Zap } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderBadge from './AnimatedBorderBadge';

const EditorialVideoShowcaseSection: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const headingColor = isDark ? '#FFFFFF' : '#111111';
  const textColor = isDark ? '#AAAAAA' : '#666666';
  const borderColor = isDark ? '#333333' : '#E0E0E0';
  const cardBgColor = isDark ? '#1A1A1A' : '#FFFFFF';
  const iconColor = isDark ? '#888888' : '#666666';
  const gridLineColor = isDark ? '#444444' : '#d0d0d0';

  const iconBoxStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
    backgroundSize: '5px 5px',
    WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
    maskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
  };

  return (
    <section 
      className="pt-16 pb-16 transition-colors duration-300"
    >
      <div className="max-w-6xl mx-auto px-8 lg:px-8">
        {/* Centered Text Content */}
        <div className="flex flex-col items-center text-center space-y-6 max-w-3xl mx-auto mb-12">
          {/* Badge with animated border */}
          <AnimatedBorderBadge text="Platform" icon={Sparkles} />
          
          <h2 
            className="text-3xl lg:text-4xl font-bold leading-[1.1] tracking-tight transition-colors duration-300"
            style={{ color: headingColor }}
          >
            Your Story In, Your Revenue Out.
          </h2>

          <p 
            className="text-sm leading-relaxed max-w-lg transition-colors duration-300" 
            style={{ color: textColor }}
          >
            Turn any lead into a loyal customer - captured, nurtured, and converted. Automatically.
          </p>
        </div>

        {/* Campaign showcase video - Centered */}
        <div className="max-w-2xl mx-auto">
          <div 
            className="relative rounded-xl overflow-hidden group"
            style={{ 
              border: `1px solid ${borderColor}`,
              boxShadow: isDark 
                ? '0 20px 40px -10px rgba(0, 0, 0, 0.5)' 
                : '0 20px 40px -10px rgba(0, 0, 0, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Mobile video - 9:16 portrait */}
            <video
              autoPlay
              muted
              loop
              playsInline
              controls
              className="md:hidden w-full h-auto"
            >
              <source 
                src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Explainer%20V6%20-%20Complete%20Overview%20Explainer.mp4" 
                type="video/mp4" 
              />
            </video>

            {/* Tablet/Desktop video - landscape */}
            <video
              autoPlay
              muted
              loop
              playsInline
              controls
              className="hidden md:block w-full h-auto"
            >
              <source 
                src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Explainer%20V6%20-%20Complete%20Overview%20Explainer.mp4" 
                type="video/mp4" 
              />
            </video>
          </div>
        </div>

        {/* Benefit Cards Grid */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {/* Get More Clients */}
          <div 
            className="rounded-2xl p-6 border text-center transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
            style={{ backgroundColor: cardBgColor, borderColor }}
          >
            <div 
              className="w-12 h-12 flex items-center justify-center rounded-xl mx-auto mb-4"
              style={iconBoxStyle}
            >
              <Target className="w-5 h-5" style={{ color: iconColor }} strokeWidth={1.5} />
            </div>
            <h3 className="text-sm font-semibold mb-2" style={{ color: headingColor }}>
              Get More Clients
            </h3>
            <p className="text-xs leading-relaxed" style={{ color: textColor }}>
              A 24/7 lead capture engine that qualifies prospects before they book a call.
            </p>
          </div>

          {/* Keep Clients Longer */}
          <div 
            className="rounded-2xl p-6 border text-center transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
            style={{ backgroundColor: cardBgColor, borderColor }}
          >
            <div 
              className="w-12 h-12 flex items-center justify-center rounded-xl mx-auto mb-4"
              style={iconBoxStyle}
            >
              <Heart className="w-5 h-5" style={{ color: iconColor }} strokeWidth={1.5} />
            </div>
            <h3 className="text-sm font-semibold mb-2" style={{ color: headingColor }}>
              Keep Clients Longer
            </h3>
            <p className="text-xs leading-relaxed" style={{ color: textColor }}>
              Automated nurture sequences and review requests that build ironclad trust.
            </p>
          </div>

          {/* Scale Your Firm */}
          <div 
            className="rounded-2xl p-6 border text-center transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
            style={{ backgroundColor: cardBgColor, borderColor }}
          >
            <div 
              className="w-12 h-12 flex items-center justify-center rounded-xl mx-auto mb-4"
              style={iconBoxStyle}
            >
              <Zap className="w-5 h-5" style={{ color: iconColor }} strokeWidth={1.5} />
            </div>
            <h3 className="text-sm font-semibold mb-2" style={{ color: headingColor }}>
              Scale Your Firm
            </h3>
            <p className="text-xs leading-relaxed" style={{ color: textColor }}>
              Workflow automation that removes the admin burden from your billable staff.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EditorialVideoShowcaseSection;
